import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Image as ImageIcon, Check } from 'lucide-react';

const LeagueIconPicker = ({ value, onChange, onClose }) => {
  const [icons, setIcons] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // List all icons from public/images/leagues
    // In production, this should be fetched from an API endpoint
    // For now, we'll use a predefined list based on the directory listing
    const iconList = [
      'great.webp',
      'ultra.webp',
      'master.webp',
      'little.webp',
      'pogo_great_league.webp',
      'pogo_ultra_league.webp',
      'pogo_master_league.webp',
      'GBL_littlecup.webp',
      'GBL_retrocup.webp',
      'cp1500_retro.webp',
      'master_premier.webp',
      'BattleIconColor.webp',
      'CombatButton.webp',
      'GlobalLeaguePennant.webp',
      'catch_cup.webp',
      'electric_cup.webp',
      'element_cup.webp',
      'flying_cup.webp',
      'fossil_gblcup.webp',
      'halloween_cup.webp',
      'summer_gblcup.webp',
      'weatherCup_icon.webp',
      'fantasy_cup_icon.webp',
      'spring_cup_icon.webp',
      'sunshine_cup_icon.webp',
      'willpower_cup_icon.webp',
      'holiday_league_icon.webp',
      'loveLeague2022_icon.webp',
      'kantoLeague2022_icon.webp',
      'johtoLeague2022_icon.webp',
      'hoenn_cup_icon.webp',
      'hisui_icon.webp',
      'elementCupRemix_icon.webp',
      'jungleCupRemix_icon.webp',
      'mountainCup_icon.webp',
      'colorCup_Icon.webp',
      'combat_blanche_icon.webp',
      'combat_candela_icon.webp',
      'combat_spark_icon.webp',
      'Icon_Blanche.webp',
      'Icon_Candela.webp',
      'Icon_Spark.webp',
      'league_icon_great_pokemon_limit.webp',
      'league_icon_ultra_pokemon_limit.webp',
      'league_icon_bidoof.webp'
    ];

    setIcons(iconList);
    setLoading(false);
  }, []);

  // Auto-detect icon from league name
  const autoDetectIcon = (leagueName) => {
    if (!leagueName) return null;

    const name = leagueName.toLowerCase();
    
    // Great League
    if (name.includes('great')) {
      return 'great.webp';
    }
    
    // Ultra League
    if (name.includes('ultra')) {
      return 'ultra.webp';
    }
    
    // Master League
    if (name.includes('master')) {
      return 'master.webp';
    }
    
    // Little Cup
    if (name.includes('little') || name.includes('little cup')) {
      return 'little.webp';
    }
    
    // Retro Cup
    if (name.includes('retro')) {
      return 'GBL_retrocup.webp';
    }
    
    // Premier
    if (name.includes('premier')) {
      return 'master_premier.webp';
    }
    
    // Cup variations
    if (name.includes('electric')) return 'electric_cup.webp';
    if (name.includes('element')) return 'element_cup.webp';
    if (name.includes('flying')) return 'flying_cup.webp';
    if (name.includes('fossil')) return 'fossil_gblcup.webp';
    if (name.includes('halloween')) return 'halloween_cup.webp';
    if (name.includes('summer')) return 'summer_gblcup.webp';
    if (name.includes('weather')) return 'weatherCup_icon.webp';
    if (name.includes('fantasy')) return 'fantasy_cup_icon.webp';
    if (name.includes('spring')) return 'spring_cup_icon.webp';
    if (name.includes('sunshine')) return 'sunshine_cup_icon.webp';
    if (name.includes('willpower')) return 'willpower_cup_icon.webp';
    if (name.includes('holiday')) return 'holiday_league_icon.webp';
    if (name.includes('love')) return 'loveLeague2022_icon.webp';
    if (name.includes('kanto')) return 'kantoLeague2022_icon.webp';
    if (name.includes('johto')) return 'johtoLeague2022_icon.webp';
    if (name.includes('hoenn')) return 'hoenn_cup_icon.webp';
    if (name.includes('hisui')) return 'hisui_icon.webp';
    if (name.includes('jungle')) return 'jungleCupRemix_icon.webp';
    if (name.includes('mountain')) return 'mountainCup_icon.webp';
    if (name.includes('color')) return 'colorCup_Icon.webp';
    if (name.includes('catch')) return 'catch_cup.webp';
    
    // Team Leaders
    if (name.includes('blanche')) return 'Icon_Blanche.webp';
    if (name.includes('candela')) return 'Icon_Candela.webp';
    if (name.includes('spark')) return 'Icon_Spark.webp';
    
    return null;
  };

  const filteredIcons = icons.filter(icon => 
    icon.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (icon) => {
    onChange(icon);
    onClose();
  };

  const handleAutoDetect = () => {
    // This will be called from parent component with league name
    // For now, just show a message
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">เลือกไอคอนลีก</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              เลือกจาก {icons.length} ไอคอนที่มีอยู่
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาไอคอน..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Icons Grid */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredIcons.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">ไม่พบไอคอนที่ตรงกับคำค้นหา</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
              {filteredIcons.map((icon) => (
                <motion.button
                  key={icon}
                  onClick={() => handleSelect(icon)}
                  className={`relative group p-3 rounded-lg border-2 transition-all ${
                    value === icon
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img
                    src={`/images/leagues/${icon}`}
                    alt={icon}
                    className="w-full h-auto rounded"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {value === icon && (
                    <div className="absolute top-1 right-1 bg-blue-500 rounded-full p-1">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-opacity" />
                  <p className="mt-2 text-xs text-center text-gray-600 dark:text-gray-400 truncate">
                    {icon.replace('.webp', '')}
                  </p>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default LeagueIconPicker;
export { LeagueIconPicker };
